import { useCallback, useEffect, useState } from 'react';
import type { ErpModule } from '@/api/platform.api';
import { notificationsApi } from '@/api';
import type { NotificationResponse } from '@/types/notification';

type UseNotificationInboxOptions = {
  module?: ErpModule;
  page?: number;
  size?: number;
  pollMs?: number;
  refreshOnFocus?: boolean;
};

export function useNotificationInbox(options: UseNotificationInboxOptions = {}) {
  const {
    module,
    page = 0,
    size = 10,
    pollMs = 60_000,
    refreshOnFocus = true,
  } = options;

  const [items, setItems] = useState<NotificationResponse[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pageRes, countRes] = await Promise.all([
        notificationsApi.list({ page, size, module }),
        notificationsApi.unreadCount(module),
      ]);
      setItems(pageRes.content ?? []);
      setUnread(countRes.count ?? 0);
      setTotalElements(pageRes.totalElements ?? 0);
      setTotalPages(pageRes.totalPages ?? 0);
    } catch {
      /* notifications optional for some roles */
    } finally {
      setLoading(false);
    }
  }, [module, page, size]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (pollMs <= 0) return undefined;
    const interval = window.setInterval(() => {
      void load();
    }, pollMs);
    return () => window.clearInterval(interval);
  }, [load, pollMs]);

  useEffect(() => {
    if (!refreshOnFocus) return undefined;
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        void load();
      }
    };
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => {
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [load, refreshOnFocus]);

  const markRead = useCallback(
    async (id: number) => {
      await notificationsApi.markRead(id);
      await load();
    },
    [load],
  );

  const markAllRead = useCallback(async () => {
    await notificationsApi.markAllRead(module);
    await load();
  }, [load, module]);

  return {
    items,
    unread,
    loading,
    totalElements,
    totalPages,
    load,
    markRead,
    markAllRead,
  };
}
